// apps/web/src/features/agent/AgentChatPanel.tsx
//
// Left-pane chat surface (#1876): the turn list (user + assistant bubbles),
// the live step-progress indicator, the verdict block, a terminal-error
// notice, and an empty state when no turns exist yet.
import { useTranslation } from 'react-i18next';
import { Icon } from '../../ui/index.js';
import type { AgentChatError, AgentStep, AgentTurn } from './useAgentChat.js';
import { ErrorCode, type AgentVerdict } from '@magimp/shared';
import { AgentStepProgress } from './AgentStepProgress.js';
import { AgentVerdictBlock } from './AgentVerdictBlock.js';

export interface AgentChatPanelProps {
  turns: AgentTurn[];
  streaming: boolean;
  step: AgentStep | null;
  verdict: AgentVerdict | null;
  error: AgentChatError | null;
}

type TFn = ReturnType<typeof useTranslation<'agent'>>['t'];

export function AgentChatPanel({ turns, streaming, step, verdict, error }: AgentChatPanelProps) {
  const { t } = useTranslation('agent');

  return (
    <div
      data-testid="agent-chat-log"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-3)',
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      {turns.length === 0 && !streaming ? (
        <div data-testid="agent-chat-empty" style={{ color: 'var(--fg-2)', fontSize: 13 }}>
          {t('chat.empty')}
        </div>
      ) : null}

      {turns.map((turn) => (
        <TurnBubble
          key={turn.id}
          turn={turn}
          streamingPlaceholder={
            turn.role === 'assistant' && turn.text.length === 0 && streaming
              ? t('chat.thinking')
              : null
          }
        />
      ))}

      {step ? <AgentStepProgress step={step} /> : null}

      {verdict ? <AgentVerdictBlock verdict={verdict} /> : null}

      {error ? <AgentErrorNotice error={error} /> : null}
    </div>
  );
}

function TurnBubble({
  turn,
  streamingPlaceholder,
}: {
  turn: AgentTurn;
  streamingPlaceholder: string | null;
}) {
  const isUser = turn.role === 'user';
  return (
    <div
      data-testid={`agent-turn-${turn.role}`}
      style={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: '85%',
        padding: 'var(--s-3)',
        borderRadius: 'var(--r-2)',
        background: isUser ? 'var(--accent-1-subtle)' : 'var(--bg-1)',
        border: '1px solid var(--border-1)',
        color: 'var(--fg-1)',
        fontSize: 13,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {streamingPlaceholder !== null ? (
        <span style={{ color: 'var(--fg-3)' }}>{streamingPlaceholder}</span>
      ) : (
        turn.text
      )}
    </div>
  );
}

function AgentErrorNotice({ error }: { error: AgentChatError }) {
  const { t } = useTranslation('agent');
  // WHY: known agent-domain failures need distinct operator/user states.
  // Unknown transport/runtime failures still collapse to the generic retry
  // message, keyed by code rather than backend free text.
  const text = errorText(error.code, t);
  return (
    <div
      role="alert"
      data-testid="agent-error"
      data-error-code={error.code}
      // WHY: `data-stage` is an operator-facing diagnostic (#2005), not user
      // copy — only present when the SSE error event carried a stage.
      {...(error.stage !== undefined ? { 'data-stage': error.stage } : {})}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--s-2)',
        padding: 'var(--s-3)',
        borderRadius: 'var(--r-2)',
        background: 'var(--status-error-bg)',
        color: 'var(--status-error-fg)',
        fontSize: 13,
      }}
    >
      <Icon name="alert-triangle" size={16} />
      <span>{text}</span>
    </div>
  );
}

function errorText(code: string, t: TFn): string {
  if (code === ErrorCode.AGENT_NOT_CONFIGURED) return t('error.notConfigured');
  if (code === ErrorCode.AGENT_SQL_REJECTED) return t('error.sqlRejected');
  if (code === ErrorCode.AGENT_SQL_TIMEOUT) return t('error.sqlTimeout');
  if (code === ErrorCode.AGENT_RUN_FAILED) return t('error.runFailed');
  return t('error.generic');
}
